// Investigation for manual enrolment

import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import PrimaryButton from "../../../Common/components/templates/buttons/primary";
import SecondaryButton from "../../../Common/components/templates/buttons/secondary";
import FormDirections from "../../../Common/components/templates/formDirections";
import FormHeader from "../../../Common/components/templates/formHeader";
import { globalConfig } from "../../../Common/configuration/config";
import { getTranslationForCode } from "../../../Common/localization/localizationHelper";
import { myAccountAudience, MY_ACCOUNT_ENROLLMENT_SCOPE } from "../../api/myAccountClient";
import usePasskeyFlow from "../../hooks/usePasskeyFlow";

/**
 * Passkey enrollment screen.
 */

const STORAGE_KEYS = {
    loginStarted: "passkeyNestedLoginStarted"
};

// TODO: hardcoded for now; previously supplied via the `connection` query param.
const PASSKEY_CONNECTION = "Username-Password-Authentication";

/** Builds the nested login callback URL. */
const buildPasskeyRedirectUri = (host: string): string => {
    return `${host.replace(/\/+$/, "")}/add-passkey`;
};

export const AddPasskey = (props: any) => {
    const params = new URLSearchParams(props.location.search);
    const config = globalConfig.get();
    const intl = useIntl();

    const isHandlingLoginCallback = params.get("code") !== null;

    // Auth0 reports a failed `/authorize` by redirecting back here with an
    // `error` parameter, NOT by rejecting a promise. Left unhandled we would
    // observe `!isAuthenticated`, call `loginWithRedirect` again, and loop
    // between this page and Auth0 indefinitely.
    const authorizeError = params.get("error");

    const { isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, error } = useAuth0();
    const { passkeyFlow, handlePasskeyEnrollment, handleCheckPasskeySupport, handlePasskeyFlowReset } = usePasskeyFlow();

    const [busy, setBusy] = useState(false);

    // The passkey slice lives in the global store and outlives this component.
    // Until it has been reset for this visit, a leftover `enrolled` or
    // `unsupported` from a previous visit must not be acted on, or the screen
    // would bounce straight back to wherever it came from.
    const [flowReset, setFlowReset] = useState(false);

    useEffect(() => {
        handlePasskeyFlowReset();
        setFlowReset(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `isAuthenticated` is NOT usable as a gate on this page.
    //
    // The SDK caches tokens keyed on {client_id, audience, scope}. Our nested
    // login uses the My Account audience, so that is the key the callback
    // writes. But `Auth0Provider` finishes initialising by calling `getUser()`
    // with no arguments, which reads the key built from the PROVIDER's default
    // audience. That is a different key, so the lookup misses, `user` is
    // undefined, and the reducer sets `isAuthenticated: false` with no error.
    //
    // The session is real and the token is cached - `isAuthenticated` is simply
    // looking in the wrong place. So we ask the question that uses the same key
    // the token was stored under: can we get a My Account token?
    const [sessionState, setSessionState] = useState<"unknown" | "ready" | "none">("unknown");

    console.log("[passkey] render", {
        isHandlingLoginCallback,
        authorizeError,
        isAuthenticated,
        sessionState,
        isLoading,
        loginStarted: sessionStorage.getItem(STORAGE_KEYS.loginStarted),
        // The SDK reports a failed redirect callback here rather than by
        // rejecting - without this the page just renders empty with no clue why.
        sdkError: error ? `${(error as any).error || error.name}: ${error.message}` : null,
        search: props.location?.search,
        // `props.location` is react-router's view, which `history.push` may have
        // already rewritten. The raw URL is what the SDK actually inspects for
        // `code`/`state`, so log it separately.
        href: window.location.href,
        // The nested login redirects to `config.Host`. If that is not this exact
        // origin the code lands on a different site and never gets exchanged.
        origin: window.location.origin,
        loginRedirectTarget: buildPasskeyRedirectUri(config.Host)
    });

    // React re-renders and strict mode can fire effects more than once. Without
    // these guards the user could be sent through `loginWithRedirect` twice, or
    // bounced to `/continue` mid-navigation.
    const hasStartedLogin = useRef(false);
    const hasLeftPage = useRef(false);

    const leave = useCallback(
        () => {
            if (hasLeftPage.current) {
                return;
            }
            hasLeftPage.current = true;

            sessionStorage.removeItem(STORAGE_KEYS.loginStarted);

            if (props.history?.length > 1) {
                props.history.goBack();
                return;
            }

            window.location.assign(config.Host);
        },
        [config.Host, props.history]
    );

    useEffect(() => {
        // Without the caller's query params there is no "fresh entry" signal, so
        // clear the loop guard once a usable session exists instead.
        if (isAuthenticated || authorizeError || sessionState === "ready") {
            sessionStorage.removeItem(STORAGE_KEYS.loginStarted);
        }
    }, [authorizeError, isAuthenticated, sessionState]);

    // ------------------------------------------------------------------
    // Step 0: abandon quietly if Auth0 refused to authorize us.
    //
    // The likeliest cause is tenant configuration rather than anything the
    // user did - notably the My Account API not being activated, which Auth0
    // reports as:
    //   access_denied / Service not found: https://{domain}/me/
    //
    // Whatever the reason, enrolment cannot proceed. Stranding the user on a
    // dead screen is worse than returning them to the caller.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!authorizeError) {
            return;
        }

        console.log(
            `[passkey] enrollment unavailable: ${authorizeError} - ${params.get("error_description") || ""}`
        );
        leave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorizeError, leave]);

    // ------------------------------------------------------------------
    // Step 1: establish a session for THIS application.
    //
    // This app may not have a session yet, so `getAccessTokenSilently` can fail
    // with `login_required`. A top-level redirect is the only way to create one:
    // `prompt=none` can reuse a session but can never establish one.
    //
    // This mirrors the other routes that bounce through Auth0 to establish a
    // browser session. The difference is the audience - those pages want the
    // identity API, this one needs My Account - so the login is issued here.
    //
    // Requesting the My Account audience up front means the token is already in
    // the SDK cache when the user presses the button, so no iframe is needed.
    // ------------------------------------------------------------------
    // ------------------------------------------------------------------
    // Step 1a: find out whether we already hold a My Account token.
    //
    // On the return leg this is a cache hit populated by the redirect callback,
    // so it resolves without a network call. On first arrival there is no
    // session, the SDK falls back to a `prompt=none` iframe, and that fails
    // with `login_required` - which is the answer we need, just more slowly.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (isLoading || authorizeError || isHandlingLoginCallback || sessionState !== "unknown") {
            return;
        }

        let cancelled = false;

        getAccessTokenSilently({
            audience: myAccountAudience(),
            scope: MY_ACCOUNT_ENROLLMENT_SCOPE
        })
            .then(() => {
                if (cancelled) {
                    return;
                }
                console.log("[passkey] My Account token available, session is usable");
                setSessionState("ready");
            })
            .catch((probeError) => {
                if (cancelled) {
                    return;
                }
                console.log("[passkey] no My Account token", {
                    error: probeError?.error || probeError?.name,
                    description: probeError?.message
                });
                setSessionState("none");
            });

        return () => {
            cancelled = true;
        };
    }, [isLoading, authorizeError, isHandlingLoginCallback, sessionState, getAccessTokenSilently]);

    useEffect(() => {
        if (
            isLoading ||
            sessionState !== "none" ||
            hasStartedLogin.current ||
            authorizeError ||
            isHandlingLoginCallback
        ) {
            return;
        }

        // A previous attempt already ran a full login round trip and we still
        // have no token. Retrying would ping-pong between this page and Auth0
        // forever, so stop and let the outer login continue.
        if (sessionStorage.getItem(STORAGE_KEYS.loginStarted) === "true") {
            console.log("[passkey] login already attempted but still no token, giving up");
            leave();
            return;
        }

        hasStartedLogin.current = true;
        sessionStorage.setItem(STORAGE_KEYS.loginStarted, "true");

        console.log("[passkey] no session yet, starting login for the My Account audience");

        loginWithRedirect({
            audience: myAccountAudience(),
            scope: MY_ACCOUNT_ENROLLMENT_SCOPE,
            redirectUri: buildPasskeyRedirectUri(config.Host)
        });
    }, [sessionState, isLoading, authorizeError, config.Host, isHandlingLoginCallback, leave, loginWithRedirect]);

    // ------------------------------------------------------------------
    // Step 2: bow out early on devices that cannot do this.
    //
    // Checking capability before showing the pitch means a user on an
    // unsupported browser is never invited to press a button that can only
    // produce a native error dialog.
    // ------------------------------------------------------------------
    useEffect(() => {
        console.log("[passkey] capability check", {
            sessionState,
            isLoading
        });
        if (sessionState === "ready" && !isLoading) {
            handleCheckPasskeySupport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionState, isLoading]);

    // ------------------------------------------------------------------
    // Step 3: react to the outcome.
    //
    // `enrolled` covers BOTH a fresh passkey and `InvalidStateError` (the
    // authenticator refusing to duplicate one it already holds). Either way,
    // this screen is finished and can return to its caller.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!flowReset) {
            return;
        }
        if (passkeyFlow.status === "enrolled") {
            leave();
        }
        if (passkeyFlow.status === "unsupported") {
            // Nothing to show and nothing to do, so quietly return to the
            // caller rather than holding the user on a dead-end screen.
            leave();
        }
        if (passkeyFlow.status === "error") {
            setBusy(false);
        }
    }, [flowReset, passkeyFlow.status, leave]);

    const handleCreate = async () => {
        setBusy(true);
        try {
            // Served from the in-memory cache populated by the redirect above,
            // so this does not need an iframe.
            const accessToken = await getAccessTokenSilently({
                audience: myAccountAudience(),
                scope: MY_ACCOUNT_ENROLLMENT_SCOPE
            });

            await handlePasskeyEnrollment(accessToken, PASSKEY_CONNECTION);
        } catch (error) {
            console.log(error);
            setBusy(false);
        }
    };

    // Nothing meaningful to render until we actually hold a My Account token -
    // flashing the pitch mid-redirect would be worse than showing nothing.
    // `authorizeError` is included because step 0 is already navigating away,
    // and `unsupported` because step 3 is.
    if (!flowReset || isLoading || sessionState !== "ready" || authorizeError || passkeyFlow.status === "unsupported") {
        return null;
    }

    const working = busy || passkeyFlow.status === "working";

    return (
        <form className="form" onSubmit={(event) => event.preventDefault()}>
            <FormHeader title={intl?.messages["passkey.header.first"]?.toString() || "Set up a passkey on this device"} />

            <FormDirections>
                {intl?.messages["passkey.description.first"]?.toString() ||
                    "Sign in faster and more securely using your fingerprint, face, or screen lock."}
            </FormDirections>

            {passkeyFlow.errorCode ? (
                <div className="error"> {getTranslationForCode(passkeyFlow.errorCode, intl)} </div>
            ) : null}

            <div className="content">
                <div className="buttons">
                    <PrimaryButton variant="contained" color="primary" onClick={handleCreate} disabled={working}>
                        {intl?.messages["passkey.button.create"]?.toString() || "Create a passkey"}
                    </PrimaryButton>
                    <SecondaryButton onClick={() => props.history.push("/reset-passkeys")} disabled={working}>
                        {intl.formatMessage({ id: "passkey.add.link.reset", defaultMessage: "Manage existing passkeys" })}
                    </SecondaryButton>
                </div>

                <FormDirections>
                    {intl?.messages["passkey.footnote"]?.toString() ||
                        "Your passkey stays on this device. Your password keeps working as usual."}
                </FormDirections>
            </div>
        </form>
    );
};

export default AddPasskey;
